import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { ArrowLeft, Upload, FileText, Users, Send, CheckCircle2, AlertCircle, Loader2, Copy, ExternalLink, Wand2, X, RefreshCw } from "lucide-react";

export default function AdminWorkspaceDetailPage() {
    const { id } = useParams();
    const { api } = useAuth();
    const [workspace, setWorkspace] = useState(null);
    const [status, setStatus] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [instances, setInstances] = useState([]);
    const [tab, setTab] = useState("candidates");
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [buildingJobTitle, setBuildingJobTitle] = useState(null);
    const [templateDetail, setTemplateDetail] = useState(null);
    const [distributing, setDistributing] = useState(false);

    const refresh = async () => {
        const [w, s, c, t, inst] = await Promise.all([
            api.get(`/v2/admin/workspaces/${id}`),
            api.get(`/v2/admin/assessments/status/${id}`),
            api.get(`/v2/admin/assessments/candidates/${id}`),
            api.get(`/v2/admin/assessments/templates/${id}`),
            api.get(`/v2/admin/assessments/instances/${id}`),
        ]);
        setWorkspace(w.data);
        setStatus(s.data);
        setCandidates(c.data.candidates);
        setTemplates(t.data.templates);
        setInstances(inst.data.instances);
    };

    useEffect(() => { refresh(); }, [id]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setUploadResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/v2/admin/assessments/onboard/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploadResult(r.data);
            await refresh();
        } catch (err) {
            setUploadResult({ error: err.response?.data?.detail || "Upload failed" });
        }
        setUploading(false);
    };

    const buildTemplate = async (jobTitle) => {
        setBuildingJobTitle(jobTitle);
        try {
            const resp = await api.post(`/v2/admin/assessments/templates/build`, {
                enterprise_id: id,
                job_title: jobTitle,
                cadre: "Practitioner-IC",
            });
            const tplId = resp.data.template_id;
            // Poll for completion (background task)
            const startedAt = Date.now();
            let done = false;
            while (!done && Date.now() - startedAt < 5 * 60 * 1000) {
                await new Promise((r) => setTimeout(r, 5000));
                try {
                    const d = await api.get(`/v2/admin/assessments/templates/detail/${tplId}`);
                    if (d.data.qa_status !== "generating") {
                        done = true;
                    }
                } catch { /* keep polling */ }
            }
            await refresh();
        } catch (err) {
            alert("Build failed: " + (err.response?.data?.detail || err.message));
        }
        setBuildingJobTitle(null);
    };

    const openTemplateDetail = async (tid) => {
        const r = await api.get(`/v2/admin/assessments/templates/detail/${tid}`);
        setTemplateDetail(r.data);
    };

    const distribute = async () => {
        setDistributing(true);
        try {
            await api.post(`/v2/admin/assessments/distribute/${id}`);
            await refresh();
        } catch (err) {
            alert("Distribute failed: " + (err.response?.data?.detail || err.message));
        }
        setDistributing(false);
    };

    const uniqueJobTitles = [...new Set(candidates.map((c) => c.job_title).filter(Boolean))];
    const templateByTitle = Object.fromEntries(templates.map((t) => [t.job_title, t]));

    const candidateLink = (token) => `${window.location.origin}/take/${token}`;

    if (!workspace) return <div className="p-8 text-sm text-gray-500">Loading workspace...</div>;

    return (
        <div className="p-8" data-testid="admin-workspace-detail">
            <Link to="/admin" className="flex items-center gap-2 text-xs text-gray-500 hover:text-white mb-6"><ArrowLeft className="w-3 h-3" /> All workspaces</Link>

            <div className="flex items-start justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-display font-bold text-white" data-testid="workspace-name">{workspace.name}</h1>
                    <p className="text-sm text-gray-500 mt-1 font-mono">{workspace.primary_industry} &middot; {workspace.subscription_tier} &middot; {workspace.users?.length || 0} users</p>
                </div>
                <button onClick={refresh} className="text-xs text-gray-500 hover:text-white flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
            </div>

            {/* Status strip */}
            {status && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
                    {[
                        { label: "Candidates", value: status.candidates, testid: "stat-candidates" },
                        { label: "Templates", value: status.templates_generated, testid: "stat-templates" },
                        { label: "TDE runs", value: status.tde_decompositions, testid: "stat-tde" },
                        { label: "Assessments sent", value: status.assessments_total, testid: "stat-sent" },
                        { label: "Completed", value: status.assessments_completed, testid: "stat-completed" },
                        { label: "Completion rate", value: status.completion_rate + "%", testid: "stat-completion-rate" },
                    ].map((s) => (
                        <div key={s.label} className="bg-card border border-border/40 p-4" data-testid={s.testid}>
                            <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">{s.label}</p>
                            <p className="text-xl font-display font-bold text-white font-mono">{s.value}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-border/40 mb-6">
                {[
                    { id: "candidates", label: `Candidates (${candidates.length})` },
                    { id: "templates", label: `Templates (${templates.length})` },
                    { id: "instances", label: `Assessments (${instances.length})` },
                ].map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                        className={`px-4 py-3 text-sm border-b-2 transition-colors ${tab === t.id ? "text-white border-white" : "text-gray-500 border-transparent hover:text-white"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Candidates tab */}
            {tab === "candidates" && (
                <div>
                    <div className="bg-card border border-border/40 p-5 mb-4">
                        <div className="flex items-center gap-4">
                            <Upload className="w-5 h-5 text-gray-500" />
                            <div className="flex-1">
                                <h3 className="text-sm font-semibold text-white">Upload Employee CSV</h3>
                                <p className="text-xs text-gray-600 mt-1">Columns: full_name, email, job_title, department, business_unit, region, employee_id, manager_email, manager_name</p>
                            </div>
                            <label className="px-4 py-2 bg-white text-black text-xs font-semibold hover:bg-gray-300 cursor-pointer transition-colors" data-testid="upload-csv-btn">
                                {uploading ? "Uploading..." : "Choose File"}
                                <input type="file" accept=".csv" className="hidden" onChange={handleUpload} disabled={uploading} />
                            </label>
                        </div>
                        {uploadResult && (
                            <div className="mt-3 p-3 border border-border/40 bg-muted text-xs text-gray-400">
                                {uploadResult.error ? (
                                    <span className="text-red-400">{uploadResult.error}</span>
                                ) : (
                                    <>
                                        <span className="text-white">{uploadResult.message}</span>
                                        {uploadResult.unique_job_titles?.length > 0 && (
                                            <div className="mt-2">Unique job titles: <span className="font-mono text-gray-300">{uploadResult.unique_job_titles.join(", ")}</span></div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {candidates.length === 0 ? (
                        <p className="text-sm text-gray-600">No candidates yet. Upload a CSV to begin.</p>
                    ) : (
                        <div className="bg-card border border-border/40 overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-muted border-b border-border/40">
                                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                        <th className="px-4 py-3">Name</th>
                                        <th className="px-4 py-3">Email</th>
                                        <th className="px-4 py-3">Job Title</th>
                                        <th className="px-4 py-3">Dept / BU</th>
                                        <th className="px-4 py-3">Region</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {candidates.map((c) => (
                                        <tr key={c.id} className="border-b border-border/40 hover:bg-white/[0.02]" data-testid={`candidate-row-${c.id}`}>
                                            <td className="px-4 py-3 text-white">{c.full_name}</td>
                                            <td className="px-4 py-3 text-gray-400 font-mono text-xs">{c.email}</td>
                                            <td className="px-4 py-3 text-gray-300">{c.job_title}</td>
                                            <td className="px-4 py-3 text-gray-500 text-xs">{c.department || c.business_unit}</td>
                                            <td className="px-4 py-3 text-gray-500 text-xs">{c.region}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Templates tab */}
            {tab === "templates" && (
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs text-gray-500">Per-job-title assessment templates. Build with Task Decomposition → Assessment Generator → QA Protocol pipeline.</p>
                    </div>
                    {uniqueJobTitles.length === 0 ? (
                        <p className="text-sm text-gray-600">Upload candidates first, then build templates for each unique job title.</p>
                    ) : (
                        <div className="space-y-2">
                            {uniqueJobTitles.map((jt) => {
                                const t = templateByTitle[jt];
                                const isBuilding = buildingJobTitle === jt;
                                return (
                                    <div key={jt} className="bg-card border border-border/40 p-4 flex items-center justify-between" data-testid={`template-row-${jt.replace(/\s+/g, "-")}`}>
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-4 h-4 text-gray-600" />
                                            <div>
                                                <p className="text-sm text-white">{jt}</p>
                                                <p className="text-xs text-gray-600 font-mono">
                                                    {t ? `${t.total_questions} questions · ${t.modules?.length || 0} modules · QA: ${t.qa_status}` : "Not generated"}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {t && (
                                                <button onClick={() => openTemplateDetail(t.id)} className="text-xs text-gray-500 hover:text-white" data-testid={`view-template-${t.id}`}>
                                                    View
                                                </button>
                                            )}
                                            <button onClick={() => buildTemplate(jt)} disabled={isBuilding}
                                                className="px-3 py-1.5 text-xs bg-white text-black hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1.5" data-testid={`build-template-${jt.replace(/\s+/g, "-")}`}>
                                                {isBuilding ? <><Loader2 className="w-3 h-3 animate-spin" /> Building...</> : <><Wand2 className="w-3 h-3" /> {t ? "Rebuild" : "Build"}</>}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Instances tab */}
            {tab === "instances" && (
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs text-gray-500">Assessment instances — one per candidate. Distribute to create magic-link tokens.</p>
                        <button onClick={distribute} disabled={distributing || candidates.length === 0}
                            className="px-4 py-2 bg-white text-black text-xs font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1.5" data-testid="distribute-btn">
                            {distributing ? <><Loader2 className="w-3 h-3 animate-spin" /> Distributing...</> : <><Send className="w-3 h-3" /> Distribute assessments</>}
                        </button>
                    </div>
                    {instances.length === 0 ? (
                        <p className="text-sm text-gray-600">No assessments distributed yet.</p>
                    ) : (
                        <div className="bg-card border border-border/40 overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-muted border-b border-border/40">
                                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                                        <th className="px-4 py-3">Candidate</th>
                                        <th className="px-4 py-3">Job Title</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Progress</th>
                                        <th className="px-4 py-3">Link</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {instances.map((inst) => (
                                        <tr key={inst.id} className="border-b border-border/40 hover:bg-white/[0.02]">
                                            <td className="px-4 py-3 text-white">{inst.candidate.full_name}</td>
                                            <td className="px-4 py-3 text-gray-400">{inst.candidate.job_title}</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs font-mono px-2 py-0.5 border ${inst.status === "completed" ? "border-green-500/30 text-green-400" : inst.status === "in_progress" ? "border-yellow-500/30 text-yellow-400" : "border-border/40 text-gray-500"}`}>
                                                    {inst.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-400 font-mono text-xs">{inst.progress_pct}%</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => { navigator.clipboard.writeText(candidateLink(inst.token)); }}
                                                        className="text-xs text-gray-500 hover:text-white flex items-center gap-1" data-testid={`copy-link-${inst.id}`}>
                                                        <Copy className="w-3 h-3" /> Copy
                                                    </button>
                                                    <a href={`/take/${inst.token}`} target="_blank" rel="noreferrer" className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
                                                        <ExternalLink className="w-3 h-3" /> Open
                                                    </a>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Template detail modal */}
            {templateDetail && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setTemplateDetail(null)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between p-6 border-b border-border/40">
                            <div>
                                <h2 className="text-lg font-display font-bold text-white">{templateDetail.job_title}</h2>
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 font-mono">
                                    <span>cadre: {templateDetail.cadre}</span>
                                    <span>v{templateDetail.version}</span>
                                    <span className={`px-2 py-0.5 border ${templateDetail.qa_status === "pass" ? "border-green-500/30 text-green-400" : templateDetail.qa_status === "fail" ? "border-red-500/30 text-red-400" : "border-yellow-500/30 text-yellow-400"}`}>
                                        QA: {templateDetail.qa_status}
                                    </span>
                                </div>
                            </div>
                            <button onClick={() => setTemplateDetail(null)} className="text-gray-600 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {templateDetail.qa_result && (
                                <div className="bg-muted border border-border/40 p-4">
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">QA Protocol</h3>
                                    <p className="text-xs text-gray-400">{templateDetail.qa_result.summary}</p>
                                    <div className="grid grid-cols-2 gap-2 mt-3">
                                        {(templateDetail.qa_result.checks || []).map((c, i) => (
                                            <div key={i} className="flex items-center gap-2 text-xs">
                                                {c.status === "PASS" ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <AlertCircle className="w-3 h-3 text-red-400" />}
                                                <span className="text-gray-300">{c.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Modules</h3>
                                {(templateDetail.content?.modules || []).map((mod, mi) => (
                                    <div key={mi} className="mb-4 border border-border/40 p-4">
                                        <h4 className="text-sm font-semibold text-white mb-1">{mod.module_name}</h4>
                                        <p className="text-xs text-gray-600 font-mono mb-3">{mod.module_id} · respondent: {mod.respondent}</p>
                                        {(mod.sections || []).map((sec, si) => (
                                            <div key={si} className="mb-3">
                                                <p className="text-xs font-semibold text-gray-300 mb-1">{sec.section_title}</p>
                                                <p className="text-xs text-gray-600 mb-2">{sec.instructions}</p>
                                                <ul className="space-y-1.5 text-xs text-gray-400">
                                                    {(sec.questions || []).map((q, qi) => (
                                                        <li key={qi} className="pl-3 border-l border-border/40">
                                                            <span className="font-mono text-gray-600">[{q.type}] </span>{q.prompt}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
